// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import { BillingPlanFilter } from '@/billing/api/get-billing-plans';
import {
  BillingPlanListItem,
  BillingPlanSortProperty,
  billingPlanSortPropertySchema,
} from '@/billing/api/types';
import { BILLING_BREADCRUMB_ROOT } from '@/billing/components/billing-nav';
import { BillingPlanActionMenu } from '@/billing/components/billing-plan-action-menu';
import { BillingPlanChargesStatBox } from '@/billing/components/billing-plan-charges-stat-box';
import { BillingPlanStatusChip } from '@/billing/components/billing-plan-status-chip';
import {
  BillingPlanStatusFilter,
  PLAN_PAGE_SIZE,
  PLAN_STATUS_FILTER_OPTIONS,
} from '@/billing/components/billing-plan-status-constants';
import { BillingPlanSummaryCards } from '@/billing/components/billing-plan-summary-cards';
import { ConfirmBillingPlanDialog } from '@/billing/components/confirm-billing-plan-dialog';
import { useBillingPlans } from '@/billing/hooks/use-billing-plans';
import { EmptyState } from '@/components/empty-state';
import {
  AmountRange,
  FilterAmountRangePopover,
} from '@/components/filter-amount-range-popover';
import { FilterMenu } from '@/components/filter-menu';
import { FilterToggleGroup } from '@/components/filter-toggle-group';
import { Link } from '@/components/link';
import { PageContent } from '@/components/page-content';
import { SectionBreadcrumbs } from '@/components/section-breadcrumbs';
import { TableControlContainer } from '@/components/table-control-container';
import { TablePaginationFooter } from '@/components/table-pagination-footer';
import {
  ArrowForwardRounded,
  MoreHorizRounded,
  SearchRounded,
} from '@/constants/hardfin-standard-icons';
import { SEARCH_TERM_MAXIMUM_CHARACTERS } from '@/constants/search-term';
import { Sort, invert, sortDirectionSchema } from '@/constants/sort';
import { formatCurrencyValue } from '@/currency/utilities/format';
import { CustomerFilterMenu } from '@/customer/components/customer-filter-menu';
import { useDisclosure } from '@/hooks/use-disclosure';
import { useTableSort } from '@/hooks/use-table-sort';
import { getSparseProjects } from '@/project/api/get-sparse-projects';
import { BillingPlanDrawerContainer } from '@/project/components/billing-plan-drawer-container';
import { pluralize } from '@/utilities/pluralize';
import { searchTermSchema } from '@/utilities/search-term';
import {
  FIFTEEN_MINUTES_MS,
  FIVE_MINUTES_MS,
  TWO_MINUTES_MS,
} from '@/utilities/time-constants';
import {
  Button,
  IconButton,
  InputAdornment,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { omit } from 'lodash-es';
import { DateTime } from 'luxon';
import { useCallback, useMemo, useRef, useState } from 'react';
import { z } from 'zod';

const visibleBillingPlanStatusSchema = z.enum([
  'PENDING',
  'CONFIRMED',
  'ACTIVE',
  'COMPLETE',
]);

const searchSchema = z.object({
  status: visibleBillingPlanStatusSchema.optional().catch(undefined),
  customerKeys: z.array(z.string()).optional(),
  projectKeys: z.array(z.string()).optional(),
  query: searchTermSchema,
  page: z.number().optional(),
  amountMin: z.number().optional().catch(undefined),
  amountMax: z.number().optional().catch(undefined),
  sortBy: billingPlanSortPropertySchema.optional().catch(undefined),
  sortDirection: sortDirectionSchema.optional(),
});

// Fallback bounds for the amount filter slider when the listing has not
// returned its amountRange yet, or when the org has zero plans. Mirrors
// the constants the charges page uses so the two listings render the
// same disabled-state placeholder.
const AMOUNT_FILTER_FALLBACK_MIN = 0;
const AMOUNT_FILTER_FALLBACK_MAX = 1;
const AMOUNT_FILTER_MIN_STEP = 1;
const AMOUNT_FILTER_STEP_DIVISOR = 100;
const AMOUNT_FILTER_SINGLE_POINT_PAD = 1;
const PLAN_CUSTOMER_COL_WIDTH = '15%';
const PLAN_PROJECT_COL_WIDTH = '20%';
const PLAN_LINE_ITEM_COL_WIDTH = '10%';
const PLAN_VALUE_COL_WIDTH = '10%';
const PLAN_CHARGES_COL_WIDTH = '20%';
const PLAN_ACTIVATION_COL_WIDTH = '10%';
const PLAN_STATUS_COL_WIDTH = '10%';
const PLAN_ACTIONS_COL_WIDTH = '5%';

// The drawer mutation's default invalidations cover the project query
// path; the billing plans listing reads from a separate query, so the
// drawer needs to invalidate it explicitly when opened from this page.
const DRAWER_INVALIDATIONS: string[][] = [['billing', 'plans']];

// Number of columns in the billing plans table — drives the cold-load
// skeleton row + empty-state colspan so they always match the
// rendered <TableHead>. Keep in sync.
const PLAN_COLUMN_COUNT = 8;

export const Route = createFileRoute('/_app/billing/plans')({
  component: BillingPlansRoute,
  validateSearch: searchSchema,
});

function BillingPlansRoute() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const activeStatus: BillingPlanStatusFilter = search.status ?? 'all';
  const activeCustomerKeys = useMemo(
    () => search.customerKeys ?? [],
    [search.customerKeys]
  );
  const activeProjectKeys = useMemo(
    () => search.projectKeys ?? [],
    [search.projectKeys]
  );
  const page = search.page ?? 1;

  const sort: Sort<BillingPlanSortProperty> | undefined = useMemo(
    () =>
      search.sortBy
        ? {
            property: search.sortBy,
            direction: search.sortDirection ?? 'asc',
          }
        : undefined,
    [search.sortBy, search.sortDirection]
  );

  const filter: BillingPlanFilter = useMemo(
    () => ({
      pageSize: PLAN_PAGE_SIZE,
      pageNumber: page,
      sort,
      search: search.query,
      status: activeStatus === 'all' ? undefined : activeStatus,
      customerKeys:
        activeCustomerKeys.length > 0 ? activeCustomerKeys : undefined,
      projectKeys: activeProjectKeys.length > 0 ? activeProjectKeys : undefined,
      amountMin: search.amountMin,
      amountMax: search.amountMax,
    }),
    [
      page,
      sort,
      search.query,
      search.amountMin,
      search.amountMax,
      activeStatus,
      activeCustomerKeys,
      activeProjectKeys,
    ]
  );

  const {
    plans,
    totalCount,
    statusSummary,
    amountRange: listingAmountRange,
    isPlaceholderData,
  } = useBillingPlans({
    filter,
  });
  const downloadURL = new URL(
    '/v1/export/billing-plans',
    import.meta.env.VITE_API_URL
  );

  // Page-overshoot recovery should live in the shared table footer instead of
  // route-specific guards. See ai/todo/pagination-footer-overshoot-recovery.md.

  const amountRange: AmountRange = [
    search.amountMin ?? null,
    search.amountMax ?? null,
  ];

  // Data-driven bounds for the amount filter slider. Mirrors the charges
  // page derivation so the two listings produce slider extents in the
  // same way.
  const amountFilterBounds = useMemo(() => {
    const fallback = {
      min: AMOUNT_FILTER_FALLBACK_MIN,
      max: AMOUNT_FILTER_FALLBACK_MAX,
      step: AMOUNT_FILTER_MIN_STEP,
      isReady: false,
    };
    if (!listingAmountRange) {
      return fallback;
    }
    const rawMin = Number(listingAmountRange.min);
    const rawMax = Number(listingAmountRange.max);
    if (Number.isNaN(rawMin) || Number.isNaN(rawMax) || rawMax < rawMin) {
      return fallback;
    }
    const integerMin = Math.floor(rawMin);
    const integerMax = Math.ceil(rawMax);
    if (integerMax === integerMin) {
      return {
        min: integerMin,
        max: integerMax + AMOUNT_FILTER_SINGLE_POINT_PAD,
        step: AMOUNT_FILTER_MIN_STEP,
        isReady: true,
      };
    }
    const step = Math.max(
      AMOUNT_FILTER_MIN_STEP,
      Math.round((integerMax - integerMin) / AMOUNT_FILTER_STEP_DIVISOR)
    );
    return { min: integerMin, max: integerMax, step, isReady: true };
  }, [listingAmountRange]);

  const handleChangeAmountRange = (next: AmountRange) => {
    void navigate({
      search: {
        ...search,
        amountMin: next[0] ?? undefined,
        amountMax: next[1] ?? undefined,
        page: undefined,
      },
    });
  };

  const isAmountFilterActive =
    search.amountMin !== undefined || search.amountMax !== undefined;

  const tableSort = useTableSort<BillingPlanSortProperty>(
    sort ?? { property: 'project', direction: 'asc' }
  );

  // Fetch sparse projects for the project filter menu (id + name only).
  // Cached for 5min — same staleness used by the charges listing.
  const { data: projectsData } = useQuery({
    queryKey: ['sparse-projects', 'filter-options'],
    queryFn: getSparseProjects,
    staleTime: FIVE_MINUTES_MS,
    gcTime: FIFTEEN_MINUTES_MS,
    refetchOnWindowFocus: true,
    refetchInterval: TWO_MINUTES_MS,
  });
  const projectOptions = (projectsData?.projects ?? []).map((p) => ({
    id: p.key,
    label: p.name,
    isActive: activeProjectKeys.includes(p.key),
  }));

  const handleChangeCustomer = (customerKeys: string[]) => {
    void navigate({
      search:
        customerKeys.length === 0
          ? { ...omit(search, 'customerKeys', 'page') }
          : { ...search, customerKeys, page: undefined },
    });
  };

  const handleChangeProject = (projectKeys: string[]) => {
    void navigate({
      search:
        projectKeys.length === 0
          ? { ...omit(search, 'projectKeys', 'page') }
          : { ...search, projectKeys, page: undefined },
    });
  };

  const handleSort = useCallback(
    (property: BillingPlanSortProperty) => {
      const newDirection =
        sort && sort.property === property ? invert(sort.direction) : 'asc';
      void navigate({
        search: {
          ...search,
          sortBy: property,
          sortDirection: newDirection,
          page: undefined,
        },
      });
    },
    [navigate, search, sort]
  );

  const handleStatusChange = (newStatus: typeof activeStatus) => {
    void navigate({
      search:
        newStatus === 'all'
          ? { ...omit(search, 'status', 'page') }
          : { ...search, status: newStatus, page: undefined },
    });
  };

  // Debounced search — mirrors the charges/invoices listings so a
  // burst of typing doesn't navigate-and-refetch on every keystroke.
  const [searchInput, setSearchInput] = useState(search.query ?? '');
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchRef = useRef(search);
  searchRef.current = search;

  const handleSearch = (query: string) => {
    setSearchInput(query);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      const latestSearch = searchRef.current;
      void navigate({
        search:
          query.length === 0
            ? { ...omit(latestSearch, 'query', 'page') }
            : { ...latestSearch, query, page: undefined },
      });
    }, 300);
  };

  const handleChangePage = (newPage: number) => {
    void navigate({
      search:
        newPage === 1
          ? { ...omit(search, 'page') }
          : { ...search, page: newPage },
    });
  };

  const handleClearFilters = () => {
    void navigate({
      search: omit(search, [
        'customerKeys',
        'projectKeys',
        'amountMin',
        'amountMax',
        'query',
        'page',
      ]),
    });
  };

  const hasActiveFilters =
    activeCustomerKeys.length > 0 ||
    activeProjectKeys.length > 0 ||
    isAmountFilterActive ||
    Boolean(search.query);

  // Action menu / dialog / drawer state. Each surface tracks its own
  // plan reference (confirmTarget / drawerPlan) so closing the menu
  // doesn't pull state out from under the dialog or drawer.
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuPlan, setMenuPlan] = useState<null | BillingPlanListItem>(null);
  const confirmDialog = useDisclosure();
  const [confirmTarget, setConfirmTarget] =
    useState<null | BillingPlanListItem>(null);
  const drawer = useDisclosure();
  const [drawerPlan, setDrawerPlan] = useState<null | BillingPlanListItem>(
    null
  );

  const handleOpenMenu = (
    event: React.MouseEvent<HTMLElement>,
    plan: BillingPlanListItem
  ) => {
    setMenuAnchor(event.currentTarget);
    setMenuPlan(plan);
  };
  const handleCloseMenu = () => {
    setMenuAnchor(null);
    setMenuPlan(null);
  };
  const handleConfirmPlan = () => {
    setConfirmTarget(menuPlan);
    setMenuAnchor(null);
    setMenuPlan(null);
    confirmDialog.open();
  };
  const handleViewBillingPlan = (plan: BillingPlanListItem) => {
    setDrawerPlan(plan);
    drawer.open();
  };

  return (
    <PageContent>
      {/* Header — breadcrumbs + title, mirrors charges page chrome */}
      <Stack sx={{ mb: 2 }}>
        <SectionBreadcrumbs
          items={[BILLING_BREADCRUMB_ROOT, { label: 'Billing plans' }]}
        />
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h5">Billing plans</Typography>
          <Button
            component={Link}
            to="/billing/charges"
            size="small"
            endIcon={<ArrowForwardRounded fontSize="small" />}
            sx={{ textTransform: 'none' }}
          >
            View charges
          </Button>
        </Stack>
      </Stack>

      <BillingPlanSummaryCards summary={statusSummary} />

      <Stack spacing={2}>
        {/* Status toggle row */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <FilterToggleGroup
            ariaLabel="Billing plan status filter"
            options={PLAN_STATUS_FILTER_OPTIONS}
            value={activeStatus}
            onChange={handleStatusChange}
          />
        </Stack>

        {/* Filter row — sits under the toggle, search anchored right */}
        <TableControlContainer paddingTop={0}>
          <Stack direction="row" spacing={2} flexGrow={1} alignItems="center">
            <CustomerFilterMenu
              label="Customer"
              identifier="key"
              onlyCustomers
              includeNotSet
              value={activeCustomerKeys}
              onChange={handleChangeCustomer}
            />
            <FilterMenu
              label="Project"
              isSearchable
              options={projectOptions}
              onChangeOption={(id, isActive) => {
                handleChangeProject(
                  isActive
                    ? [...activeProjectKeys, id]
                    : activeProjectKeys.filter((p) => p !== id)
                );
              }}
            />
            <FilterAmountRangePopover
              label="Amount"
              min={amountFilterBounds.min}
              max={amountFilterBounds.max}
              step={amountFilterBounds.step}
              isDisabled={!amountFilterBounds.isReady}
              value={amountRange}
              onChange={handleChangeAmountRange}
            />
            {hasActiveFilters && (
              <Button size="small" onClick={handleClearFilters}>
                Clear filters
              </Button>
            )}
          </Stack>
          <TextField
            size="small"
            placeholder="Search billing plans"
            value={searchInput}
            inputProps={{ maxLength: SEARCH_TERM_MAXIMUM_CHARACTERS }}
            onChange={(event) => {
              handleSearch(event.target.value);
            }}
            sx={{ minWidth: 240 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRounded
                    fontSize="small"
                    sx={{ color: 'neutral.400' }}
                  />
                </InputAdornment>
              ),
            }}
          />
        </TableControlContainer>

        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ mt: '0 !important' }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                {/* Customer first, then Project — matches the filter
                    row order so users scan the table the same way they
                    set the filters. Mirrors the charges listing. */}
                <SortHeader
                  label="Customer"
                  property="customer"
                  tableSort={tableSort}
                  onSort={handleSort}
                  width={PLAN_CUSTOMER_COL_WIDTH}
                />
                <SortHeader
                  label="Project"
                  property="project"
                  tableSort={tableSort}
                  onSort={handleSort}
                  width={PLAN_PROJECT_COL_WIDTH}
                />
                <TableCell width={PLAN_LINE_ITEM_COL_WIDTH}>
                  Line item
                </TableCell>
                <TableCell width={PLAN_VALUE_COL_WIDTH} align="right">
                  <SortInline
                    label="Line item total"
                    property="contractValue"
                    tableSort={tableSort}
                    onSort={handleSort}
                  />
                </TableCell>
                <TableCell width={PLAN_CHARGES_COL_WIDTH}>Charges</TableCell>
                <SortHeader
                  label="Activation date"
                  property="activationDate"
                  tableSort={tableSort}
                  onSort={handleSort}
                  width={PLAN_ACTIVATION_COL_WIDTH}
                />
                <SortHeader
                  label="Status"
                  property="status"
                  tableSort={tableSort}
                  onSort={handleSort}
                  width={PLAN_STATUS_COL_WIDTH}
                />
                <TableCell width={PLAN_ACTIONS_COL_WIDTH} />
              </TableRow>
            </TableHead>
            <TableBody>
              {(plans === null || (plans.length === 0 && isPlaceholderData)) &&
                // Cold load OR a filter swap that has produced an empty
                // placeholder result while the new fetch is in flight —
                // render skeleton rows that match the real layout so the
                // table doesn't shift on first paint and we don't flash
                // an empty state we'll immediately replace.
                Array.from({ length: PLAN_PAGE_SIZE }).map((_, idx) => (
                  <TableRow key={`skeleton-${idx}`}>
                    {Array.from({ length: PLAN_COLUMN_COUNT }).map(
                      (__, col) => (
                        <TableCell key={col}>
                          <Skeleton variant="text" />
                        </TableCell>
                      )
                    )}
                  </TableRow>
                ))}
              {plans !== null && plans.length === 0 && !isPlaceholderData && (
                <TableRow>
                  <TableCell colSpan={PLAN_COLUMN_COUNT}>
                    <EmptyState
                      title={
                        activeStatus === 'PENDING'
                          ? 'No pending billing plans'
                          : activeStatus === 'CONFIRMED'
                            ? 'No plans in the confirmed state'
                            : activeStatus === 'ACTIVE'
                              ? 'No plans currently billing'
                              : activeStatus === 'COMPLETE'
                                ? 'No complete billing plans'
                                : 'No billing plans yet'
                      }
                      message={
                        activeStatus === 'all'
                          ? 'Billing plans are created from the project detail page'
                          : 'Try a different filter or clear filters to see all plans'
                      }
                    />
                  </TableCell>
                </TableRow>
              )}
              {plans !== null &&
                plans.map((plan) => (
                  <TableRow key={plan.lineItemKey} hover>
                    <TableCell>{plan.customerName ?? '—'}</TableCell>
                    <TableCell>{plan.projectName}</TableCell>
                    <TableCell>
                      {/* SKU with the full item name on hover, plus a
                          quantity caption underneath — mirrors the
                          line-item cell on the charges listing so the
                          two tables read the same way. */}
                      <Stack spacing={0.25}>
                        <Tooltip title={plan.itemName} arrow placement="top">
                          <Typography
                            variant="body2"
                            component="span"
                            sx={{
                              display: 'inline-block',
                              alignSelf: 'flex-start',
                              borderBottom: '1px dotted',
                              borderColor: 'text.secondary',
                            }}
                          >
                            {plan.itemSku}
                          </Typography>
                        </Tooltip>
                        <Typography variant="caption" color="text.secondary">
                          {pluralize(plan.quantity, { singular: 'unit' })}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrencyValue(plan.contractValue)}
                    </TableCell>
                    <TableCell>
                      <BillingPlanChargesStatBox plan={plan} />
                    </TableCell>
                    <TableCell>
                      {plan.activationDate
                        ? formatDate(plan.activationDate)
                        : '—'}
                    </TableCell>
                    <TableCell>
                      <BillingPlanStatusChip status={plan.status} />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={(event) => handleOpenMenu(event, plan)}
                      >
                        <MoreHorizRounded fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePaginationFooter
          pagination={{
            page,
            pageSize: PLAN_PAGE_SIZE,
            onPageChange: handleChangePage,
          }}
          totalCount={totalCount}
          entityOptions={{ singular: 'billing plan', includeCount: false }}
          downloadURL={downloadURL}
          downloadLabel="Export billing plans"
        />
      </Stack>

      <BillingPlanActionMenu
        menuAnchor={menuAnchor}
        menuPlan={menuPlan}
        onClose={handleCloseMenu}
        onConfirmPlan={handleConfirmPlan}
        onViewBillingPlan={handleViewBillingPlan}
      />
      <ConfirmBillingPlanDialog
        isOpen={confirmDialog.isOpen}
        onClose={confirmDialog.close}
        plan={confirmTarget}
      />
      <BillingPlanDrawerContainer
        isOpen={drawer.isOpen}
        onClose={drawer.close}
        projectKey={drawerPlan?.projectKey ?? null}
        lineItemKey={drawerPlan?.lineItemKey ?? null}
        additionalInvalidations={DRAWER_INVALIDATIONS}
      />
    </PageContent>
  );
}

function formatDate(value: DateTime | null): string {
  return (
    value?.toLocaleString({
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }) ?? '—'
  );
}

function SortHeader<T extends string>(props: {
  label: string;
  property: T;
  tableSort: ReturnType<typeof useTableSort<T>>;
  onSort: (property: T) => void;
  width?: string;
}) {
  return (
    <TableCell
      width={props.width}
      sortDirection={
        props.tableSort.isLabelActive(props.property)
          ? props.tableSort.getLabelDirection(props.property)
          : false
      }
    >
      <TableSortLabel
        active={props.tableSort.isLabelActive(props.property)}
        direction={props.tableSort.getLabelDirection(props.property)}
        onClick={() => props.onSort(props.property)}
      >
        {props.label}
      </TableSortLabel>
    </TableCell>
  );
}

function SortInline<T extends string>(props: {
  label: string;
  property: T;
  tableSort: ReturnType<typeof useTableSort<T>>;
  onSort: (property: T) => void;
}) {
  return (
    <TableSortLabel
      active={props.tableSort.isLabelActive(props.property)}
      direction={props.tableSort.getLabelDirection(props.property)}
      onClick={() => props.onSort(props.property)}
    >
      {props.label}
    </TableSortLabel>
  );
}
